import { useCallback, useState } from "react"
import { useNavigate } from "react-router-dom"
import { enqueueSnackbar } from "notistack"
import { Skeleton, Stack } from "@mui/material"
import { makeStyles } from "tss-react/mui"

import { getEditMenuTemplateRoute } from "../../../../../../constants/routes"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { ErrorDisplay } from "../../../../../../core/api/errorTypes"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { DeleteConfirmDialog } from "../../../../../../shared/components/dialogs/DeleteConfirmDialog"
import { StandardNoResultText } from "../../../../../../shared/components/noResult/StandardNoResultText"
import { useMerchant } from "../../../../../merchants/merchantHooks"

import { deleteMenuTemplate, fetchMenuTemplates, MenuTemplatesQuery, MenuTemplatesResponse } from "../menuTemplatesApi"
import { MenuTemplateId } from "../menuTemplatesTypes"
import { TemplateListRow } from "../components/TemplateListRow"

const useStyles = makeStyles()(() => ({
    rowSkeleton: {
        height: "4.5rem",
        borderRadius: ".625rem",
    },
}))

const SKELETON_ROWS = 3

const selectMenuTemplates = (data: MenuTemplatesResponse) => data.menuTemplates ?? []

const defaultConfirmModalState = {
    showConfirmModal: false,
    confirmModalText: "",
    itemId: null,
}

export function MenuTemplatesTab() {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()
    const navigate = useNavigate()
    const { merchantId } = useMerchant()

    const [confirmModalState, setConfirmModalState] = useState<{
        showConfirmModal: boolean
        confirmModalText: string
        itemId: null | MenuTemplateId
    }>(defaultConfirmModalState)

    const templatesQuery = useApiQuery(fetchMenuTemplates, {
        queryName: MenuTemplatesQuery.LIST,
        dependencies: { merchantId },
        select: selectMenuTemplates,
        errorDisplay: ErrorDisplay.NONE,
    })
    const templates = templatesQuery.data ?? []

    const { mutate: callDeleteTemplate } = useApiMutation(deleteMenuTemplate, {
        async onSuccess(_params, data, context) {
            await context.queryClient.invalidateQueries({ queryKey: [MenuTemplatesQuery.LIST] })
            enqueueSnackbar({
                variant: "success",
                message: data.serviceResponse.displayMessage,
            })
        },
    })

    const handleDeleteClick = useCallback(() => {
        if (confirmModalState.itemId != null) {
            callDeleteTemplate({ templateId: confirmModalState.itemId })
        } else {
            throw new Error("Menu template id is undefined")
        }
    }, [confirmModalState.itemId, callDeleteTemplate])

    return (
        <Stack spacing=".75rem">
            {templatesQuery.isInitialLoading &&
                Array.from({ length: SKELETON_ROWS }).map((_, index) => (
                    <Skeleton key={index} variant="rectangular" className={classes.rowSkeleton} />
                ))}

            {!templatesQuery.isInitialLoading && templatesQuery.isError && (
                <StandardNoResultText noResultText={l10n.getString("merchant-menu-templates-list-load-error")} />
            )}

            {!templatesQuery.isInitialLoading && !templatesQuery.isError && templates.length === 0 && (
                <StandardNoResultText noResultText={l10n.getString("merchant-menu-templates-empty")} />
            )}

            {templates.map((template) => (
                <TemplateListRow
                    key={template.id}
                    template={template}
                    onOpen={() => navigate(getEditMenuTemplateRoute(template.id))}
                    onRemove={() =>
                        setConfirmModalState({
                            showConfirmModal: true,
                            confirmModalText: template.name,
                            itemId: template.id,
                        })
                    }
                />
            ))}

            {confirmModalState.showConfirmModal && (
                <LocalizedStrict id="merchant-menu-templates-delete-entity" attrs={{ typeOfEntity: true }}>
                    <DeleteConfirmDialog
                        typeOfEntity="menu template"
                        entityName={confirmModalState.confirmModalText}
                        handleClick={handleDeleteClick}
                        onClose={() => setConfirmModalState(defaultConfirmModalState)}
                        open={confirmModalState.showConfirmModal}
                    />
                </LocalizedStrict>
            )}
        </Stack>
    )
}
